import { useEffect } from "react";
import { useHistory } from "react-router";

import { FlightError } from "@b2bportal/air-booking-api";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  type PriceFreezeError,
  PriceFreezeErrorEnum,
} from "@b2bportal/price-freeze-api";
import {
  Payment,
  type PaymentError,
  Product,
  type ProductError,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { UberPaymentError } from "@hopper-b2b/api";
import {
  AirChfarExerciseDiscountSelectors,
  AirPFExerciseSelectors,
  CartFulfillErrorModal,
  CartFulfillState,
  CartQuoteEventType,
  cartQuoteSelectors,
  getChildState,
  useCheckoutSend,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CHFAR_ID_QUERY_PARAM } from "@hopper-b2b/types";
import { ErrorModal, LoadingScreenWithProgress } from "@hopper-b2b/ui";
import { getEnvVariables, useUberBridge } from "@hopper-b2b/utilities";

import Briefcase from "../../../../assets/client/briefcase-detail.svg";
import Error from "../../../../assets/client/error.svg";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import { cartFulfillSelectors } from "../../states/CartFulfill";
import type { FlightMachineContext } from "../../types";
import { PriceFreezeErrorModal } from "../PriceFreezeErrorModal";
import "./styles.scss";
import { UserActionComponent } from "./UserActionComponent";

export const CartFulfillComponent = (props) => {
  const { setHeader } = useUberBridge();
  const { t } = useI18nContext();
  const [state] = useCheckoutState<TEvent, FlightMachineContext>();
  const childState = getChildState(state.value);

  useEffect(() => {
    switch (childState) {
      case CartFulfillState.schedule || CartFulfillState.polling:
        setHeader({
          title: t("bookingFlight"),
        });
        break;
    }
  }, [childState, setHeader, t]);

  const loadingSteps = [
    {
      title: t("bookingFlight"),
      description: t("bookingFlightSubtitle"),
      image: Briefcase,
    },
  ];

  const callFailed = useSelector(
    cartFulfillSelectors.getOpenCartFulfillErrorModal
  );
  const callSuccessful = useSelector(cartFulfillSelectors.getCartFulfillError)
    ?.data?.[0];
  const loading = !(callFailed || callSuccessful);

  // If we need another action from the user, show them the modal/iframe
  const needsUserAction = childState === CartFulfillState.userAction;

  return (
    <>
      {needsUserAction ? (
        <UserActionComponent />
      ) : (
        <LoadingScreenWithProgress
          className="schedule-fulfill-loading"
          loadingSteps={loadingSteps}
          averageLoadingTime={
            getEnvVariables(
              needsUserAction
                ? "cartFulfillLoadingTime"
                : "cartFulfillLoadingTime"
            ) as number
          }
          loading={loading}
        />
      )}
      <MappedErrorModal />
    </>
  );
};

export const CartErrorModal = () => {
  const cartErrorModal = useSelector(cartFulfillSelectors.getCartFulfillError);
  const open = useSelector(cartFulfillSelectors.getOpenCartFulfillErrorModal);

  const showPriceFreezeErrorModal = (
    type?: CartFulfillErrorModal,
    data?: PurchaseError[]
  ) => {
    switch (type) {
      case CartFulfillErrorModal.FailedPolling: {
        const head = data?.[0]; // todo: we probably want to have some sort of priority order on errors?
        if (!head) return false;

        switch (head.Error) {
          case PurchaseErrorEnum.NoAvailability:
            return true;
          case PurchaseErrorEnum.ProductError: {
            const productError = head as ProductError;
            if (productError.value.type === Product.Flight) {
              const flightsError = productError.value.value as FlightError;
              return flightsError === FlightError.NoAvailability;
            }
            return false;
          }
          default:
            return false;
        }
      }
      default:
        return false;
    }
  };

  const priceFreezeId = useSelector(AirPFExerciseSelectors.getPriceFreezeId);
  const shouldShowPriceFreezeErrorModal =
    priceFreezeId &&
    showPriceFreezeErrorModal(
      cartErrorModal?.type,
      cartErrorModal?.data as PurchaseError[]
    );

  return shouldShowPriceFreezeErrorModal ? (
    <PriceFreezeErrorModal openModal={open} />
  ) : (
    <MappedErrorModal />
  );
};

export const MappedErrorModal = () => {
  const { t } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<TEvent>();
  const cartErrorModal = useSelector(cartFulfillSelectors.getCartFulfillError);
  const open = useSelector(cartFulfillSelectors.getOpenCartFulfillErrorModal);
  const chfarId = useSelector(AirChfarExerciseDiscountSelectors.getChfarId);

  const clearError = () => send(Event.CLEAR_SCHEDULE_FULFILL_ERROR);

  const searchAgain = () => {
    clearError();
    //ChFAR exercise
    if (chfarId)
      history.push(`${PATH_FLIGHTS_SEARCH}?${CHFAR_ID_QUERY_PARAM}=${chfarId}`);
    else history.push(PATH_FLIGHTS_SEARCH);
  };
  const updateTravelers = () => {
    clearError();
    send(Event.OPEN_PASSENGER_PICKER);
  };
  const goToConfirmation = () => {
    clearError();
    send(Event.GO_TO_BOOKING_CONFIRMATION);
  };

  const allowedQuoteRetry = useSelector(cartQuoteSelectors.getAllowQuoteRetry);
  const retryQuote = () => {
    send(CartQuoteEventType.RETRY_QUOTE);
  };
  const retryQuoteOrSearchAgain = {
    ...(allowedQuoteRetry
      ? {
          primaryButtonText: t("checkoutError.tryAgain"),
          primaryOnClick: retryQuote,
          secondaryButtonText: t("checkoutError.findNewFlight"),
          secondaryOnClick: searchAgain,
        }
      : {
          primaryButtonText: t("checkoutError.findNewFlight"),
          primaryOnClick: searchAgain,
        }),
  };
  const DEFAULT_ERROR_MODEL_PROPS = {
    title: t("checkoutError.genericErrorTitle"),
    subtitle: t("checkoutError.genericErrorSubtitle"),
    ...retryQuoteOrSearchAgain,
  };

  const getFlightError = (flightError: FlightError) => {
    switch (flightError) {
      case FlightError.CheckInMinimumAgeNotMet:
        return {
          title: t("checkoutError.checkInMinimumAgeNotMetTitle"),
          subtitle: t("checkoutError.checkInMinimumAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.DuplicateBooking:
        return {
          title: t("checkoutError.duplicateBookingTitle"),
          subtitle: t("checkoutError.duplicateBookingSubtitle"),
          primaryButtonText: t("checkoutError.acknowlegement"),
          primaryOnClick: goToConfirmation,
        };
      case FlightError.ExpiringPassport:
        return {
          title: t("checkoutError.expiringPassportTitle"),
          subtitle: t("checkoutError.expiringPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ExpiredPassport:
        return {
          title: t("checkoutError.expiredPassportTitle"),
          subtitle: t("checkoutError.expiredPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.IllegalLapInfantKTN:
        return {
          title: t("checkoutError.illegalLapInfantKTNTitle"),
          subtitle: t("checkoutError.illegalLapInfantKTNSubtitle"), // todo: we don't have the pax name
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.IllegalLapInfantRedress:
        return {
          title: t("checkoutError.illegalLapInfantRedressTitle"),
          subtitle: t("checkoutError.illegalLapInfantRedressSubtitle"), // todo: we don't have the pax name
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.Inactivity:
        // inactivity here means that the flight booking session timed out
        return {
          title: t("checkoutError.flightsInActivityTitle"),
          subtitle: t("checkoutError.flightsInActivitySubtitle"),
          primaryButtonText: t("checkoutError.tryAgain"),
          ...retryQuoteOrSearchAgain,
        };
      case FlightError.InvalidCustomer:
        return {
          title: t("checkoutError.invalidCustomerTitle"),
          subtitle: t("checkoutError.invalidCustomerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengers:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassengerName:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerName"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.InvalidPassportNumber:
        return {
          title: t("checkoutError.invalidPassportNumberTitle"),
          subtitle: t("checkoutError.invalidPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.LapInfantTooOld:
        return {
          title: t("checkoutError.lapInfantTooOldTitle"),
          subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.LapInfantsUnsupported:
        return {
          title: t("checkoutError.lapInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.lapInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MalformedKnownTravelerNumber:
        return {
          title: t("checkoutError.malformedKnownTravelerNumberTitle"),
          subtitle: t("checkoutError.malformedKnownTravelerNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MalformedRedressNumber:
        return {
          title: t("checkoutError.malformedRedressNumberTitle"),
          subtitle: t("checkoutError.malformedRedressNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MissingAirlineLocator:
        return {
          title: t("checkoutError.missingAirlineLocatorTitle"),
          subtitle: t("checkoutError.missingAirlineLocatorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.MissingNationality:
        return {
          title: t("checkoutError.missingNationalityTitle"),
          subtitle: t("checkoutError.missingNationalitySubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.MissingPassport:
        return {
          title: t("checkoutError.missingPassportTitle"),
          subtitle: t("checkoutError.missingPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.MissingPassportNumber:
        return {
          title: t("checkoutError.missingPassportNumberTitle"),
          subtitle: t("checkoutError.missingPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoAdultPassenger:
        return {
          title: t("checkoutError.noAdultPassengerTitle"),
          subtitle: t("checkoutError.noAdultPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoContactInformation:
        return {
          title: t("checkoutError.noContactInformationTitle"),
          subtitle: t("checkoutError.noContactInformationSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"), // todo: update contact info
          primaryOnClick: updateTravelers,
        };
      case FlightError.NoTicketlessResponse:
        return {
          title: t("checkoutError.noTicketlessResponseTitle"),
          subtitle: t("checkoutError.noTicketlessResponseSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.PassengerNameTooShort:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.passengerNameTooShortSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.ProviderError:
        return {
          title: t("checkoutError.providerErrorTitle"),
          subtitle: t("checkoutError.providerErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.SeatedInfantsUnsupported:
        return {
          title: t("checkoutError.seatedInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TicketingNotStarted:
        return {
          title: t("checkoutError.ticketingNotStartedTitle"),
          subtitle: t("checkoutError.ticketingNotStartedSubtitle"),
          primaryButtonText: "", // todo close CTA
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TicketingUnconfirmed:
        return {
          title: t("checkoutError.ticketingUnconfirmedTitle"),
          subtitle: t("checkoutError.ticketingUnconfirmedSubtitle"),
          primaryButtonText: "", // todo go to confirmation page
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TicketingUnknown:
        return {
          title: t("checkoutError.ticketingUnknownTitle"),
          subtitle: t("checkoutError.ticketingUnknownSubtitle"),
          primaryButtonText: "", // todo go to confirmation page
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TooManyLapInfants:
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TooManyPassengers:
        return {
          title: t("checkoutError.tooManyPassengersTitle"),
          subtitle: t("checkoutError.tooManyPassengersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.TransientServiceError:
        return {
          title: t("checkoutError.transientServiceErrorTitle"),
          subtitle: t("checkoutError.transientServiceErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.UnknownSabreAppError:
        return {
          title: t("checkoutError.unknownSabreAppErrorTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      case FlightError.UnsupportedNameCharacters:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.unsupportedNameCharactersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTravelers,
        };
      case FlightError.UpgradeRequired:
        return {
          title: t("checkoutError.upgradeRequiredTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      // default FlightErrors that should not occur in cartFulfill, or no specific error messaging needed
      case FlightError.DuplicatedNationalId:
      case FlightError.DuplicatedPassport:
      case FlightError.ExpiredNationalId:
      case FlightError.ExpiringNationalId:
      case FlightError.ExtraBagsError:
      case FlightError.InvalidPassportOrNationalIdNumber:
      case FlightError.MissingNationalId:
      case FlightError.MissingPassportOrNationalId:
      case FlightError.NoAvailability:
      case FlightError.SessionClosed:
      case FlightError.ShopAsyncError:
      case FlightError.Unknown:
        return DEFAULT_ERROR_MODEL_PROPS;
      default:
        // Will throw a compiler error when there are more FlightError's added to the api
        return ensureExhaustive(flightError, DEFAULT_ERROR_MODEL_PROPS);
    }
  };

  const getAirPriceFreezeError = (pfError: PriceFreezeError) => {
    switch (pfError.PriceFreezeError) {
      case PriceFreezeErrorEnum.NotEnoughSeats:
        return DEFAULT_ERROR_MODEL_PROPS;
      case PriceFreezeErrorEnum.OfferNotFound:
        return DEFAULT_ERROR_MODEL_PROPS;
      case PriceFreezeErrorEnum.PriceFreezeErrorCode:
        return DEFAULT_ERROR_MODEL_PROPS;
      case PriceFreezeErrorEnum.ProductNotFound:
        return DEFAULT_ERROR_MODEL_PROPS;
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getProductError = (error: ProductError) => {
    switch (error.value.type) {
      case Product.AirPriceFreeze:
        return getAirPriceFreezeError(error.value.value as PriceFreezeError);
      case Product.Flight:
      case Product.MultiProviderFlight:
        return getFlightError(error.value.value as FlightError);
      case Product.Seats:
        // https://github.com/hopper-org/b2b-interfaces/blob/8316205c08da3b4405647ff5245f3bdc76f0ef73/purchase/src/main/scala/com/hopper/b2b/purchase/Models.scala#L114
        return getFlightError(error.value.value as FlightError);
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getUberPaymentError = (error: UberPaymentError) => {
    switch (error) {
      case UberPaymentError.QuoteIdMismatch:
        return DEFAULT_ERROR_MODEL_PROPS;
      case UberPaymentError.UnknownFailure:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getPaymentError = (error: PaymentError) => {
    switch (error.value.type) {
      case Payment.AirPriceFreezeExerciseDiscount:
        return getAirPriceFreezeError(error.value.value as PriceFreezeError);
      case Payment.UberPayment:
        return getUberPaymentError(error.value.value as UberPaymentError);
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getErrorModalProps = (
    type?: CartFulfillErrorModal,
    data?: PurchaseError[]
  ) => {
    switch (type) {
      case CartFulfillErrorModal.FailedPolling: {
        const head = data?.[0]; // todo: we probably want to have some sort of priority order on errors?
        switch (head?.Error) {
          case PurchaseErrorEnum.NoAvailability:
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          case PurchaseErrorEnum.PaymentError:
            return getPaymentError(head as PaymentError);
          case PurchaseErrorEnum.ProductError: // todo. Missing `Error` attribute
            return getProductError(head as ProductError);
          case PurchaseErrorEnum.ExpiredFulfillToken:
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: searchAgain,
            };
          default:
            // default case covers
            // - PurchaseErrorEnum.AuthError
            // - PurchaseErrorEnum.ExpiredQuoteToken
            // - PurchaseErrorEnum.FraudAutoReject
            // - PurchaseErrorEnum.InActivity
            // - PurchaseErrorEnum.InvalidSession
            // - PurchaseErrorEnum.NoPaymentsProvided
            // - PurchaseErrorEnum.NonDistinctPayments
            // - PurchaseErrorEnum.ErrorCode (because this isn't standardized enough yet)
            return DEFAULT_ERROR_MODEL_PROPS;
        }
      }
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };
  const errorModalProps = getErrorModalProps(
    cartErrorModal?.type,
    cartErrorModal?.data as PurchaseError[]
  );

  return <ErrorModal open={open} {...errorModalProps} image={Error} />;
};
